import { GlossaryEntry, GlossaryEntrySchema, initialGlossaryData } from './types';

const STORAGE_KEY = 'psa_glossary_data';
const VERSION_KEY = 'psa_glossary_version';
const CURRENT_VERSION = '1.0';

class GlossaryStorage {
  private static instance: GlossaryStorage;

  private constructor() {
    this.initializeStorage();
  }

  public static getInstance(): GlossaryStorage {
    if (!GlossaryStorage.instance) {
      GlossaryStorage.instance = new GlossaryStorage();
    }
    return GlossaryStorage.instance;
  }

  private initializeStorage(): void {
    try {
      const version = localStorage.getItem(VERSION_KEY);
      if (version !== CURRENT_VERSION) {
        this.setEntries(initialGlossaryData);
        localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
      }

      // Validate existing data
      const entries = this.getEntries();
      this.setEntries(entries); // Re-save validated data
    } catch (error) {
      console.error('Storage initialization error:', error);
      this.setEntries(initialGlossaryData);
      localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
    }
  }

  public getEntries(): GlossaryEntry[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return initialGlossaryData;

      const entries = JSON.parse(data);
      return entries.map((entry: unknown) => GlossaryEntrySchema.parse(entry));
    } catch (error) {
      console.error('Error retrieving glossary entries:', error);
      return initialGlossaryData;
    }
  }

  public setEntries(entries: GlossaryEntry[]): void {
    try {
      const validatedEntries = entries.map(entry => GlossaryEntrySchema.parse(entry));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(validatedEntries));
    } catch (error) {
      console.error('Error saving glossary entries:', error);
    }
  }

  public addEntry(entry: GlossaryEntry): void {
    const entries = this.getEntries();
    this.setEntries([...entries, entry]);
  }

  public updateEntry(entry: GlossaryEntry): void {
    const entries = this.getEntries();
    const updatedEntries = entries.map(e => 
      e.id === entry.id ? entry : e
    );
    this.setEntries(updatedEntries);
  }

  public deleteEntry(id: string): void {
    const entries = this.getEntries();
    this.setEntries(entries.filter(e => e.id !== id));
  }
}

export const glossaryStorage = GlossaryStorage.getInstance();